/**
 * Плавающий «+» SMS-центра — общий с CRM и HUB (владелец 19.10.2026: «один общий плюсик,
 * устроен как в CRM»). Типы, подписи и чистые функции; зеркало frontend/src/lib/feedback.ts
 * и support.ts (импорт между пакетами запрещён). Запросы — api.ts (через узкий прокси Go
 * go/retention/crm_feedback_proxy.go на CRM-ручки /api/feedback*).
 */

export type FeedbackKind = "bug" | "idea" | "question";
export type FeedbackStatus =
  | "new"
  | "triage"
  | "agreed"
  | "progress"
  | "verify"
  | "done"
  | "rejected";
export type FeedbackSeverity = "block" | "annoy" | "minor";

/** Виды обращений: подпись и цвет-токен. */
export const FB_KINDS: Record<FeedbackKind, { label: string; tone: string }> = {
  bug: { label: "Ошибка", tone: "var(--fk-error)" },
  idea: { label: "Идея", tone: "var(--fk-success-text)" },
  question: { label: "Вопрос", tone: "var(--fk-info)" },
};

/** Статусы CRM: Новое → Разбираемся → (Согласовано) → В работе → На проверке → Готово / Отклонено. */
export const FB_STATUSES: Record<FeedbackStatus, { label: string; tone: string }> = {
  new: { label: "Новое", tone: "var(--fk-info)" },
  triage: { label: "Разбираемся", tone: "var(--fk-orange-hover)" },
  agreed: { label: "Согласовано", tone: "var(--fk-chart-5)" },
  progress: { label: "В работе", tone: "var(--fk-chart-6)" },
  verify: { label: "На проверке у партнёра", tone: "var(--fk-warning-text)" },
  done: { label: "Готово", tone: "var(--fk-success-text)" },
  rejected: { label: "Отклонено", tone: "var(--fk-text-muted)" },
};

export const statusOf = (k: string): { label: string; tone: string } =>
  FB_STATUSES[k as FeedbackStatus] ?? FB_STATUSES.new;

/** Подпись статуса для автора: «На проверке у партнёра» → «Проверьте, пожалуйста». */
export const authorStatusLabel = (k: string): string =>
  k === "verify" ? "Проверьте, пожалуйста" : statusOf(k).label;

/** «Насколько мешает» (только ошибка): подписи и подсказки как в CRM. */
export const FB_SEVERITY: Record<
  FeedbackSeverity,
  { label: string; hint: string; tone: string }
> = {
  block: { label: "Мешает работать", hint: "не могу сделать дело", tone: "var(--fk-error)" },
  annoy: { label: "Неудобно", hint: "есть обходной путь", tone: "var(--fk-orange-hover)" },
  minor: { label: "Мелочь", hint: "опечатка, вид, цвет", tone: "var(--fk-text-muted)" },
};

// ───────── Ответы CRM ─────────

export interface WidgetInfo {
  enabled: boolean;
  texts: { bugIntro: string; ideaIntro: string; slaText: string };
  counts: { verify: number; unread: number; open: number };
  ideasPublic?: boolean;
  isTeam?: boolean;
  /** Вопрос в УК включён. Нет поля (старый бэкенд) — считаем включённым. */
  questionEnabled?: boolean;
}

export interface FeedbackEvent {
  id: string;
  type: string;
  internal: boolean;
  authorName: string | null;
  text: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
}

export interface FeedbackAttachment {
  id: string;
  kind: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  eventId: string | null;
  createdAt: string;
}

export interface MyTicket {
  id: string;
  code: string;
  kind: FeedbackKind;
  status: FeedbackStatus;
  statusLabel: string;
  title: string;
  section: string | null;
  severity: FeedbackSeverity | null;
  isMine: boolean;
  authorName: string | null;
  authorUnread: boolean;
  rejectReason: string | null;
  createdAt: string;
  updatedAt: string;
  // Только у вопросов в УК: отдел, ответственный, срок ответа.
  deptLabel?: string | null;
  assigneeName?: string | null;
  taken?: boolean;
  dueAt?: string | null;
  firstResponseAt?: string | null;
  urgent?: boolean;
}

export interface MyTicketDetail extends MyTicket {
  body: string | null;
  expected: string | null;
  events: FeedbackEvent[];
  attachments: FeedbackAttachment[];
}

export type RoutedBy = "keywords" | "ai" | "manual" | "fallback";

/** Ответ `POST /feedback/route-preview` — «Кому уйдёт вопрос». */
export interface RoutingResult {
  deptCode: string;
  deptLabel: string;
  routedBy: RoutedBy;
  hits: string[];
  alternatives: Array<{ code: string; label: string }>;
  ai?: { confidence: number; reason: string };
  urgent: boolean;
  assignee: { id: string; name: string; why: string } | null;
  context: string[];
  slaMinutes: number;
  dueAt: string;
  kb: { title: string; url: string } | null;
}

export interface SupportDepartment {
  code: string;
  label: string;
  hint: string | null;
  slaText: string;
}

export interface CreateFeedbackPayload {
  kind: FeedbackKind;
  title?: string;
  body: string;
  expected?: string;
  severity?: FeedbackSeverity;
  section: string;
  pageUrl: string;
  context?: Record<string, unknown>;
  /** Вопрос в УК: отдел, выбранный вручную («Не туда? Выбрать отдел самому»). */
  deptCode?: string;
}

/**
 * Что показывать в «+»: по ответу `GET /feedback/widget`. Нет ответа или ошибка — как «включено»
 * (отправка сама скажет, если что не так); `enabled: false` — CRM выключила виджет роли;
 * `questionEnabled: false` — убран только «Вопрос в УК», нет поля (бэкенд старше) — он есть.
 */
export function feedbackGate(
  info: Pick<WidgetInfo, "enabled" | "questionEnabled"> | undefined,
): { feedbackOn: boolean; questionOn: boolean } {
  const feedbackOn = info?.enabled !== false;
  return { feedbackOn, questionOn: feedbackOn && info?.questionEnabled !== false };
}

// ───────── Подписи ─────────

/** «3 ч назад», «вчера», «5 дн назад» — как в CRM. */
export function ago(iso: string, now: number = Date.now()): string {
  const h = Math.round((now - new Date(iso).getTime()) / 3600000);
  if (h < 1) return "только что";
  if (h < 24) return `${h} ч назад`;
  const d = Math.round(h / 24);
  return d === 1 ? "вчера" : `${d} дн назад`;
}

/** «4 окт 16:05». */
export function fmtDT(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" })} ${d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`;
}

/** Подпись записи ленты: «сменил(а) статус: В работе», «ответил(а) партнёру»… */
export function eventWhat(e: FeedbackEvent, kind?: FeedbackKind): string {
  switch (e.type) {
    case "created":
      return e.meta?.manual
        ? "внёс(ла) обращение со слов партнёра"
        : `отправил(а) ${(kind ? FB_KINDS[kind].label : "обращение").toLowerCase()}`;
    case "status":
      return `статус: ${statusOf(e.toStatus ?? "").label}${e.meta?.auto ? " (после первого ответа)" : ""}`;
    case "reply":
      return "ответил(а) партнёру";
    case "note":
      return "заметка для команды";
    case "comment":
      return "написал(а)";
    case "assign":
      return "изменил(а) свойства";
    case "merge":
      return "объединение";
    case "system":
      return "определила отдел и срок";
    case "verify_ok":
      return "проверил(а): всё работает";
    case "verify_fail":
      return "проверил(а): не исправлено";
    default:
      return "событие";
  }
}

// ───────── Файлы ─────────

/**
 * Потолок вложения в SMS-центре — 4,5 МБ, а не 10 как в CRM: nginx sms.brasol.ru режет тело
 * запроса на 5 МБ (client_max_body_size в deploy/nginx-sms.conf), а Go-прокси пропускает до 11 МБ.
 * Поднимете лимит в nginx — поднимите и это число (до 10, CRM больше не примет).
 */
export const FEEDBACK_FILE_MAX_MB = 4.5;
export const FEEDBACK_FILES_MAX = 5;
const ALLOWED_FILE = /^image\/(png|jpeg|webp)$|^application\/pdf$/;

/** Проверка файла перед отправкой: текст ошибки или null. */
export function fileProblem(
  f: { name: string; type: string; size: number },
  maxMb: number = FEEDBACK_FILE_MAX_MB,
): string | null {
  if (!ALLOWED_FILE.test(f.type)) {
    return `«${f.name}»: можно только картинку (PNG, JPG, WEBP) или PDF`;
  }
  if (f.size > maxMb * 1024 * 1024) {
    return `«${f.name}» больше ${String(maxMb).replace(".", ",")} МБ`;
  }
  return null;
}

/** Раздел обращения на доске CRM: «SMS-центр · <страница>». Длина — как у колонки (160). */
export const crmSection = (page: string): string =>
  `SMS-центр · ${page.replace(/\s+/g, " ").trim() || "Без раздела"}`.slice(0, 160);

/** Технический контекст без ПДн (бэкенд ещё раз чистит его sanitizeContext). */
export function clientContext(
  section: string,
  role: string | null,
): Record<string, string> {
  let timezone = "";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* старый браузер */
  }
  const nav = typeof navigator !== "undefined" ? navigator : null;
  return {
    version: "SMS-центр",
    section,
    ...(role ? { role } : {}),
    browser: nav?.userAgent.slice(0, 120) ?? "",
    language: nav?.language ?? "",
    viewport:
      typeof window !== "undefined"
        ? `${window.innerWidth}×${window.innerHeight}`
        : "",
    timezone,
  };
}
